import { c as _c } from "react/compiler-runtime";
import { IcoShare16 } from '@onefootprint/icons';
import type { LanguageDefinitionResponse, TemplateAutocomplete } from '@onefootprint/request-types/dashboard';
import { Dialog } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExpressionEditor } from '@/components/expression-editor';
import { CelNodeFormInstructions } from '../cel-node-form-instructions';
export type ExpandableExpressionEditorLabels = {
  /** Accessible name of the button that opens the expanded editor. */
  expand: string;
  dialogTitle: string;
  subtitle: ReactNode;
  celSubtitle: string;
  instructions: string;
};
type ExpandableExpressionEditorProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  placeholder?: string;
  autocompleteItems?: TemplateAutocomplete;
  languageDefinition?: LanguageDefinitionResponse;
  labels: ExpandableExpressionEditorLabels;
};

/** Dialog edits buffer into a draft and only commit on Save, so Cancel discards them. */
const ExpandableExpressionEditor = t0 => {
  const $ = _c(34);
  const {
    value,
    onChange,
    disabled,
    hasError,
    placeholder,
    autocompleteItems,
    languageDefinition,
    labels
  } = t0;
  const {
    t: tCommon
  } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  let t1;
  if ($[0] !== value) {
    t1 = () => {
      setDraft(value);
      setIsOpen(true);
    };
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const openDialog = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsOpen(false);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const closeDialog = t2;
  let t3;
  if ($[3] !== draft || $[4] !== onChange) {
    t3 = () => {
      onChange(draft);
      closeDialog();
    };
    $[3] = draft;
    $[4] = onChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleSave = t3;
  const t4 = !disabled;
  let t5;
  if ($[6] !== t4) {
    t5 = cx({
      "group-hover:pr-8 group-focus-within:pr-8": t4
    });
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== autocompleteItems || $[9] !== disabled || $[10] !== hasError || $[11] !== languageDefinition || $[12] !== onChange || $[13] !== placeholder || $[14] !== t5 || $[15] !== value) {
    t6 = <ExpressionEditor value={value} onChange={onChange} autocompleteItems={autocompleteItems} languageDefinition={languageDefinition} disabled={disabled} placeholder={placeholder} variant="input" hasError={hasError} className={t5} />;
    $[8] = autocompleteItems;
    $[9] = disabled;
    $[10] = hasError;
    $[11] = languageDefinition;
    $[12] = onChange;
    $[13] = placeholder;
    $[14] = t5;
    $[15] = value;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== disabled || $[18] !== labels || $[19] !== openDialog) {
    t7 = !disabled && <button type="button" aria-label={labels.expand} className="hidden items-center justify-center w-6 h-6 rounded-(--icon-button-border-radius-tiny) border border-tertiary cursor-pointer hover:bg-hover-control absolute right-1 top-1/2 -translate-y-1/2 group-hover:flex group-focus-within:flex" onClick={openDialog}><IcoShare16 /></button>;
    $[17] = disabled;
    $[18] = labels;
    $[19] = openDialog;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== autocompleteItems || $[22] !== draft || $[23] !== handleSave || $[24] !== isOpen || $[25] !== labels || $[26] !== languageDefinition || $[27] !== placeholder || $[28] !== tCommon) {
    t8 = isOpen && <Dialog open={isOpen} onClose={closeDialog} size="large" title={labels.dialogTitle} primaryButton={{
      label: tCommon(_temp),
      onClick: handleSave
    }} secondaryButton={{
      label: tCommon(_temp2),
      onClick: closeDialog
    }}><div className="flex flex-col gap-4"><CelNodeFormInstructions subtitle={labels.subtitle} celSubtitle={labels.celSubtitle} instructionsLabel={labels.instructions} /><ExpressionEditor value={draft} onChange={setDraft} autocompleteItems={autocompleteItems} languageDefinition={languageDefinition} variant="textarea" placeholder={placeholder} className="h-[280px]!" /></div></Dialog>;
    $[21] = autocompleteItems;
    $[22] = draft;
    $[23] = handleSave;
    $[24] = isOpen;
    $[25] = labels;
    $[26] = languageDefinition;
    $[27] = placeholder;
    $[28] = tCommon;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== t6 || $[31] !== t7 || $[32] !== t8) {
    t9 = <div className="relative group">{t6}{t7}{t8}</div>;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
};
export default ExpandableExpressionEditor;
function _temp($) {
  return $.save;
}
function _temp2($_0) {
  return $_0.cancel;
}